/* eslint-disable react/jsx-props-no-spreading */
import { arrayOf, bool, func, node, number, oneOfType, shape, string } from 'prop-types';
import React, { Fragment, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import Plus from 'yoda-design-tokens/assets/icons/navigation/plus.svg';
import Icon from '../../Icon';

const Accordion = ({
    automationId,
    children,
    className,
    iconSize,
    onClick,
    panelLeftIndent,
    sectionUnderline,
    toggleControlLeft,
    toggleOnlyOne,
}) => {
    const [expanded, setExpanded] = useState([]);

    if (expanded.length !== children.length) {
        let shouldExpand = false;
        let expandedCount = 0;

        const updatedExpanded = children.map((item) => {
            if (toggleOnlyOne) {
                /*
                 * If toggleOnlyOne is true and multiple panels have expanded
                 * set true then only expand the first expanded panel
                 */
                if (shouldExpand) expandedCount += 1;
                shouldExpand = expandedCount > 0 ? false : item.expanded;

                return shouldExpand;
            }

            return item.expanded || false;
        });

        setExpanded(updatedExpanded);
    }

    const renderHeader = (header, index) => {
        const handleClick = (event) => {
            const updatedExpanded = toggleOnlyOne
                ? Array(children.length).fill(false)
                : [...expanded];

            onClick && onClick(event);

            updatedExpanded[index] = toggleOnlyOne ? !expanded[index] : !updatedExpanded[index];
            setExpanded(updatedExpanded);
        };

        const config = {
            button: {
                'aria-controls': `accordion-panel-${index}`,
                'aria-expanded': expanded[index],
                className: 'flex items-center py-2 w-full',
                id: `accordion-button-${index}`,
                onClick: handleClick,
                tabIndex: '0',
            },
            icon: {
                'aria-hidden': 'true',
                children: <Plus />,
                className: `${toggleControlLeft ? 'mr-2' : 'ml-auto'}`,
                focusable: 'false',
                height: iconSize,
                style: {
                    transform: `rotate(${expanded[index] ? '-45' : '90'}deg)`,
                    transition: 'transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                },
                width: iconSize,
            },
            text: {
                className: 'font-open-sans-semibold text-medium',
            },
        };

        return (
            <div aria-level="3" role="heading">
                <button {...config.button} type="button">
                    {toggleControlLeft && <Icon {...config.icon} />}
                    <span {...config.text}>{header}</span>
                    {!toggleControlLeft && <Icon {...config.icon} />}
                </button>
            </div>
        );
    };

    return (
        <div
            className={className}
            data-automation-id={automationId}
            data-component-name="Accordion"
        >
            {children.map(({ header, panel }, index) => (
                /*
                 * Ran into an issue using a dynamic key causing AnimateHeight to
                 * not respect the duration prop. The key needs to be stable in order
                 * for AnimateHeight (and React in general) to function properly.
                 * Please see this link for additional reference:
                 * https://stackoverflow.com/questions/51371953/react-collapse-transition-doesnt-animate-when-wrapped-in-a-div-with-a-dynamic-k#answer-51380169
                 */
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={index}>
                    {renderHeader(header, index)}
                    <AnimateHeight
                        contentClassName={`pb-2 relative text-small${
                            toggleControlLeft && panelLeftIndent ? ' ml-4' : ''
                        }`}
                        duration={600}
                        height={expanded[index] ? 'auto' : 0}
                    >
                        {panel}
                    </AnimateHeight>
                    <hr
                        className={`border-0 my-0${
                            sectionUnderline ? ' border-b-1 border-solid border-gray-200' : ''
                        }`}
                    />
                </Fragment>
            ))}
        </div>
    );
};

Accordion.propTypes = {
    /*
     * Children accepts an array of section objects.
     * For examples reference Storybook or accordion.stories.js.
     */
    children: arrayOf(
        shape({
            expanded: bool,
            header: oneOfType([node, number, string]).isRequired,
            panel: oneOfType([node, number, string]).isRequired,
        })
    ).isRequired,
    /*
     * Automation testing ID.
     */
    automationId: string,
    /*
     * Allows custom classes to be applied.
     */
    className: string,
    /*
     * Sets size of toggle icon.
     */
    iconSize: string,
    /*
     * Allows custom additional functionality when opening and closing panels.
     */
    onClick: func,
    /*
     * If panelLeftIndent is false and toggleControlLeft is true,
     * panel is not indented to align with header.
     */
    panelLeftIndent: bool,
    /*
     * If sectionUnderline is false supress each section underline.
     */
    sectionUnderline: bool,
    /*
     * If toggleControlLeft is true the icon to expand/collapse sections
     * is rendered to the left of the header.
     */
    toggleControlLeft: bool,
    /*
     * If toggleOnlyOne is true only one section at a time can be expanded.
     * If multiple sections have the 'expanded' option set to true, only
     * the first of those sections is expanded when Accordion is rendered.
     */
    toggleOnlyOne: bool,
};

Accordion.defaultProps = {
    automationId: null,
    className: null,
    iconSize: '20',
    onClick: () => {},
    panelLeftIndent: true,
    sectionUnderline: true,
    toggleControlLeft: false,
    toggleOnlyOne: false,
};

export default Accordion;
